import {useMemo} from "react";
import {Line, YAxis} from "recharts";
import {BaseChart, ChartComponent} from "./BaseChart";
import {TimeSeriesDataProps} from "../../../types/timeSeriesDataProps";
import {FieldResult} from "../../../types/domain/fieldResult";
import {useMeasureSystemUtils} from "../../../hooks/measureSystemUtilsHooks";
import {MeasureSystemUtils} from "../../../types/domain/measureSystemUtils";
import {useTranslation} from "react-i18next";
import dayjs from "dayjs";

export const LeafNitrogenChart: ChartComponent = ({data}) => {
    const {t} = useTranslation();
    const msUtils = useMeasureSystemUtils();
    const chartData = useMemo(() => data.map((d) => mapFieldResultsToChartData(d, msUtils)), [data, msUtils]);

    return (
        <BaseChart
            title={t("insights.charts.leaf_nitrogen.title")}
            yLabel={t("insights.charts.leaf_nitrogen.y_label")}
            data={chartData}
            helpPopoverTranslationKey="help.popover.fields.leaf_nitrogen"
        >
            <Line
                type="monotone"
                dataKey="nitrogenLeafPercentage"
                name={t("insights.charts.leaf_nitrogen.line1_name")}
                unit="%"
                yAxisId="left"
                stroke="#FD28FC"
                fill="#FD28FC"
                strokeWidth={2}
                dot={false}
                isAnimationActive={false}
            />
            <Line
                type="monotone"
                dataKey="nitrogenLeafProduction"
                name={t("insights.charts.leaf_nitrogen.line2_name")}
                unit={msUtils.getProductionUnits()}
                yAxisId="right"
                stroke="#404DB8"
                fill="#404DB8"
                strokeWidth={2}
                dot={false}
                isAnimationActive={false}
            />
            <YAxis
                yAxisId="right"
                domain={["auto", "auto"]}
                fontSize={14}
                label={{
                    value: `${t("insights.charts.leaf_nitrogen.y_axis")}, ${msUtils.getProductionUnits()}`,
                    angle: 90,
                    dx: 20,
                    dy: 0,
                    textAnchor: "middle",
                }}
                orientation="right"
            />
        </BaseChart>
    );
};

const mapFieldResultsToChartData = (r: FieldResult, msUtils: MeasureSystemUtils): DataProps => ({
    date: dayjs(r.date).toDate(),
    nitrogenLeafPercentage: r.aggregatedProps.n_leaf_percentage,
    nitrogenLeafProduction: msUtils.getProduction(r.aggregatedProps.n_leaf_production),
});

export interface DataProps extends TimeSeriesDataProps {
    nitrogenLeafPercentage: number;
    nitrogenLeafProduction: number;
}
